import { useState } from "react";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  Trash2Icon,
  GripVerticalIcon,
  VideoIcon,
  FileQuestionIcon,
  LightbulbIcon,
  UsersIcon,
  ClipboardListIcon,
  FileTextIcon,
  XIcon,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { AcademicianButton } from "@/components/academician/academician-button";
import {
  ContentPickerDialog,
  ContentItem,
  ContentType,
} from "@/components/academician/content-picker-dialog";

export interface ChapterItemData {
  id?: number;
  item_type: ContentType;
  item_id: number;
  title: string;
  duration?: string;
  questionsCount?: number;
  scheduledDate?: string;
  weekNumber?: number;
  activityType?: string;
  fileCount?: number;
}

export interface ChapterData {
  id?: number;
  title: string;
  items: ChapterItemData[];
}

interface ChapterEditorCardProps {
  chapter: ChapterData;
  index: number;
  totalChapters: number;
  onUpdate: (chapter: ChapterData) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  availableVideos: ContentItem[];
  availableQuizzes: ContentItem[];
  availableBrainstormingSessions: ContentItem[];
  availableMeetupSessions: ContentItem[];
  availableActivities: ContentItem[];
  availableDocuments: ContentItem[];
}

export function ChapterEditorCard({
  chapter,
  index,
  totalChapters,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  availableVideos,
  availableQuizzes,
  availableBrainstormingSessions,
  availableMeetupSessions,
  availableActivities,
  availableDocuments,
}: ChapterEditorCardProps) {
  const [pickerType, setPickerType] = useState<ContentType | null>(null);

  const alreadyAddedIds = chapter.items.map((item) => item.item_id);

  const handleTitleChange = (title: string) => {
    onUpdate({ ...chapter, title });
  };

  const handleAddContent = (contentItem: ContentItem) => {
    const newItem: ChapterItemData = {
      item_type: contentItem.type,
      item_id: contentItem.id,
      title: contentItem.title,
      duration: contentItem.duration,
      questionsCount: contentItem.questionsCount,
      scheduledDate: contentItem.scheduledDate,
      weekNumber: contentItem.weekNumber,
      fileCount: contentItem.fileCount,
    };
    onUpdate({ ...chapter, items: [...chapter.items, newItem] });
  };

  const handleRemoveItem = (itemIndex: number) => {
    const newItems = chapter.items.filter((_, i) => i !== itemIndex);
    onUpdate({ ...chapter, items: newItems });
  };

  const handleMoveItemUp = (itemIndex: number) => {
    if (itemIndex === 0) return;
    const newItems = [...chapter.items];
    [newItems[itemIndex - 1], newItems[itemIndex]] = [
      newItems[itemIndex],
      newItems[itemIndex - 1],
    ];
    onUpdate({ ...chapter, items: newItems });
  };

  const handleMoveItemDown = (itemIndex: number) => {
    if (itemIndex === chapter.items.length - 1) return;
    const newItems = [...chapter.items];
    [newItems[itemIndex], newItems[itemIndex + 1]] = [
      newItems[itemIndex + 1],
      newItems[itemIndex],
    ];
    onUpdate({ ...chapter, items: newItems });
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Chapter Header */}
      <div className="flex items-center gap-3 p-4 border-b border-border">
        <GripVerticalIcon className="h-5 w-5 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium text-muted-foreground shrink-0">
          {index + 1}.
        </span>
        <Input
          value={chapter.title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Chapter title"
          className="flex-1"
        />
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-1.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move up"
          >
            <ChevronUpIcon className="h-4 w-4" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={index === totalChapters - 1}
            className="p-1.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move down"
          >
            <ChevronDownIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (confirm("Delete this chapter and all its content?")) {
                onDelete();
              }
            }}
            className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors"
            title="Delete chapter"
          >
            <Trash2Icon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Chapter Items */}
      <div className="p-4">
        {chapter.items.length > 0 ? (
          <div className="space-y-2 mb-4">
            {chapter.items.map((item, itemIndex) => (
              <div
                key={`${item.item_type}-${item.item_id}-${itemIndex}`}
                className="flex items-center gap-3 p-2.5 rounded-md border border-border bg-background"
              >
                {item.item_type === "Video" ? (
                  <VideoIcon className="h-4 w-4 text-blue-500 shrink-0" />
                ) : item.item_type === "Quiz" ? (
                  <FileQuestionIcon className="h-4 w-4 text-purple-500 shrink-0" />
                ) : item.item_type === "MeetupSession" ? (
                  <UsersIcon className="h-4 w-4 text-green-500 shrink-0" />
                ) : item.item_type === "Activity" ? (
                  <ClipboardListIcon className="h-4 w-4 text-emerald-500 shrink-0" />
                ) : item.item_type === "Document" ? (
                  <FileTextIcon className="h-4 w-4 text-orange-500 shrink-0" />
                ) : (
                  <LightbulbIcon className="h-4 w-4 text-amber-500 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {item.title}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {item.item_type === "BrainstormingSession"
                        ? "Brainstorming"
                        : item.item_type === "MeetupSession"
                          ? "Meetup"
                          : item.item_type === "Activity"
                            ? "Activity"
                            : item.item_type === "Document"
                              ? "Document"
                              : item.item_type}
                    </span>
                    {item.duration && (
                      <span className="text-xs text-muted-foreground">
                        {item.duration}
                      </span>
                    )}
                    {item.questionsCount !== undefined && (
                      <span className="text-xs text-muted-foreground">
                        {item.questionsCount} questions
                      </span>
                    )}
                    {item.weekNumber !== undefined && (
                      <span className="text-xs text-muted-foreground">
                        Week {item.weekNumber}
                      </span>
                    )}
                    {item.scheduledDate && (
                      <span className="text-xs text-muted-foreground">
                        {item.scheduledDate}
                      </span>
                    )}
                    {item.activityType && (
                      <span className="text-xs text-muted-foreground">
                        {item.activityType}
                      </span>
                    )}
                    {item.fileCount !== undefined && (
                      <span className="text-xs text-muted-foreground">
                        {item.fileCount} file{item.fileCount === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    onClick={() => handleMoveItemUp(itemIndex)}
                    disabled={itemIndex === 0}
                    className="p-1 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move up"
                  >
                    <ChevronUpIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleMoveItemDown(itemIndex)}
                    disabled={itemIndex === chapter.items.length - 1}
                    className="p-1 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move down"
                  >
                    <ChevronDownIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleRemoveItem(itemIndex)}
                    className="p-1 rounded hover:bg-destructive/10 text-destructive transition-colors"
                    title="Remove"
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-3 mb-4">
            No content yet. Add videos, quizzes, brainstorming, meetup
            sessions, activities, or documents below.
          </p>
        )}

        {/* Add Content Buttons */}
        <div className="flex gap-2">
          <AcademicianButton
            variant="secondary"
            size="sm"
            leftIcon={<VideoIcon className="h-3.5 w-3.5" />}
            onClick={() => setPickerType("Video")}
          >
            Add Video
          </AcademicianButton>
          <AcademicianButton
            variant="secondary"
            size="sm"
            leftIcon={<FileQuestionIcon className="h-3.5 w-3.5" />}
            onClick={() => setPickerType("Quiz")}
          >
            Add Quiz
          </AcademicianButton>
          <AcademicianButton
            variant="secondary"
            size="sm"
            leftIcon={<LightbulbIcon className="h-3.5 w-3.5" />}
            onClick={() => setPickerType("BrainstormingSession")}
          >
            Add Brainstorming
          </AcademicianButton>
          <AcademicianButton
            variant="secondary"
            size="sm"
            leftIcon={<UsersIcon className="h-3.5 w-3.5" />}
            onClick={() => setPickerType("MeetupSession")}
          >
            Add Meetup
          </AcademicianButton>
          <AcademicianButton
            variant="secondary"
            size="sm"
            leftIcon={<ClipboardListIcon className="h-3.5 w-3.5" />}
            onClick={() => setPickerType("Activity")}
          >
            Add Validation
          </AcademicianButton>
          <AcademicianButton
            variant="secondary"
            size="sm"
            leftIcon={<FileTextIcon className="h-3.5 w-3.5" />}
            onClick={() => setPickerType("Document")}
          >
            Add Document
          </AcademicianButton>
        </div>
      </div>

      {/* Content Picker Dialog */}
      {pickerType && (
        <ContentPickerDialog
          open={true}
          onClose={() => setPickerType(null)}
          onSelect={handleAddContent}
          items={
            pickerType === "Video"
              ? availableVideos
              : pickerType === "Quiz"
                ? availableQuizzes
                : pickerType === "MeetupSession"
                  ? availableMeetupSessions
                  : pickerType === "Activity"
                    ? availableActivities
                    : pickerType === "Document"
                      ? availableDocuments
                      : availableBrainstormingSessions
          }
          alreadyAddedIds={alreadyAddedIds}
          contentType={pickerType}
        />
      )}
    </div>
  );
}
